import { useEscape } from "@/hooks/useKeyboardShortcut";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { newCrossIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { Search } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

interface InputFieldProps {
  value: string;
  onChange: (value: string) => void;
  dismissSearch?: () => void;
  type: "email" | "search" | "default";
  placeholder?: string;
  isManuallyAddingPlayer?: boolean;
  children?: React.ReactNode;
  autoFocus?: boolean;
  readOnly?: boolean;
  showSearch?: boolean;
  handleIsSearching?: (isSearching: boolean) => void;
  shouldUseOutsideClick?: boolean;
  large?: boolean;
  className?: string;
}

export const InputField = ({
  value,
  onChange,
  dismissSearch,
  type,
  placeholder,
  children,
  autoFocus,
  readOnly,
  showSearch,
  handleIsSearching,
  large,
  className,
}: InputFieldProps) => {
  const [hasClicked, setHasClicked] = useState(showSearch || false);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const inputRef = useRef<HTMLInputElement>(null);
  const [focus, setFocus] = useState(false);

  const ref = useOutsideClick(() => {
    setHasClicked(false);
    handleIsSearching?.(false);
  });
  useEscape(() => {
    dismissSearch?.();
    setHasClicked(false);
    handleIsSearching?.(false);
  });

  useEffect(() => {
    if (focus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focus]);

  useEffect(() => {
    return () => {
      handleIsSearching?.(false);
    };
  }, []);

  return (
    <div ref={ref}>
      {isMobile && type === "search" && !hasClicked && !showSearch ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setHasClicked(true);
            handleIsSearching?.(true);
          }}
          className="w-9 h-15 flex items-center justify-center"
        >
          <Search className="h-5 w-5 text-gray-400" />
        </button>
      ) : (
        <div
          className={cn(
            "w-full h-9 flex items-center px-2 bg-new-gray-850 rounded-sm",
            !large && "touch-device:h-7",
            className,
          )}
        >
          {type === "search" && (
            <Search className="h-5 w-5 text-gray-400 mr-1 shrink-0" />
          )}
          <input
            ref={inputRef}
            placeholder={placeholder}
            value={value}
            autoFocus={autoFocus}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 min-w-0 bg-transparent outline-none placeholder:text-dark-tertiary w-40 text-dark-secondary"
          />
          {children}
          {(hasClicked || value) && !readOnly && value !== "" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
                setFocus(true);
              }}
              className="w-4 h-4 text-white hover:opacity-80"
            >
              {newCrossIcon}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
